/**
 * useImproveInMultiAgent — shared escalation hook for the "Improve in Multi-Agent"
 * flow that appears in the shared report views (PlaybookRunReportView,
 * AgentRunReportView) and artifact lists (AgentArtifactList / ArtifactList).
 *
 * Owns three things that were copy-pasted across four call sites:
 *   1. The sessionStorage key and navigation URL (single source of truth)
 *   2. The improving/loading boolean state
 *   3. The sessionStorage write + navigate sequence
 *
 * Each caller provides `buildPayload` at *call time* (not hook init), so
 * different callers can use their own API clients for artifact fetching without
 * requiring stable callback references or wrapping everything in useCallback.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { getActiveOrgId, type OrgId } from "@/lib/org-scope";

export const IMPROVE_HANDOFF_KEY = "ncoder:improve-handoff";
export const IMPROVE_HANDOFF_URL = "/weave?seed=agent-result";

export interface ImproveHandoffPayload {
  source: "agent" | "migration" | "debugger";
  agentName?: string | null;
  agentGoal?: string | null;
  report?: string | null;
  runId?: string | null;
  artifacts?: { name: string; content: string }[];
}

/** What actually goes into sessionStorage: the payload plus its owning org. */
interface StoredImproveHandoff extends ImproveHandoffPayload {
  orgId: OrgId | null;
}

/**
 * Park an "improve this in Weave" payload for the multi-agent route to pick up.
 *
 * This crosses a navigation, so it is the one piece of org-scoped state that
 * deliberately outlives the screen that produced it — which makes it a race:
 * the user can switch organizations between the write and the read. The payload
 * carries a full agent report and artifact bodies, so landing it in the wrong
 * organization's composer would attach one org's source code to another org's
 * prompt.
 *
 * Stamping the organization at write and checking it at read is the storage
 * equivalent of an `OrgToken`: the handoff is honoured only by the organization
 * that created it.
 */
export function writeImproveHandoff(payload: ImproveHandoffPayload): void {
  const stored: StoredImproveHandoff = { ...payload, orgId: getActiveOrgId() };
  try {
    sessionStorage.setItem(IMPROVE_HANDOFF_KEY, JSON.stringify(stored));
  } catch { /* private browsing or storage quota exceeded */ }
}

/**
 * Take the pending handoff, if it belongs to the organization now on screen.
 *
 * Always consumes the entry — a handoff for another organization is discarded,
 * not left behind to be picked up later by a third switch.
 */
export function consumeImproveHandoff(): ImproveHandoffPayload | null {
  let raw: string | null = null;
  try { raw = sessionStorage.getItem(IMPROVE_HANDOFF_KEY); } catch { /* unavailable */ }
  if (!raw) return null;
  try { sessionStorage.removeItem(IMPROVE_HANDOFF_KEY); } catch { /* ignore */ }

  try {
    const stored = JSON.parse(raw) as StoredImproveHandoff;
    // A payload written before this stamp existed has no orgId. Refusing it is
    // the safe reading: we cannot tell which organization's report it holds.
    if (!stored.orgId || stored.orgId !== getActiveOrgId()) return null;
    return stored;
  } catch {
    return null; // malformed handoff
  }
}

export function useImproveInMultiAgent() {
  const [improving, setImproving] = useState(false);
  const navigate = useNavigate();

  const handoff = async (buildPayload: () => Promise<ImproveHandoffPayload>) => {
    setImproving(true);
    try {
      const payload = await buildPayload();
      writeImproveHandoff(payload);
      navigate(IMPROVE_HANDOFF_URL);
    } finally {
      setImproving(false);
    }
  };

  return { handoff, improving };
}
